import type { QueryClient } from '@tanstack/react-query';

import { localStorage } from '@change-corgi/core/storage';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	DraftClient,
	findOrCreateDraftAuthorId,
	InMemoryAdapter,
	LocalStorageAdapter,
	type PetitionDraft,
} from '#src/app/shared/api/petitionDraft';
import { generateAiPetitionImage, type PetitionAngleData } from '#src/app/shared/api/socialStarter';

export type DraftContext = Readonly<{
	authorId: string;
	draftClient: DraftClient;
}>;

export type ImagePreparation = Readonly<{
	imageGenerationCount: number;
	imageJobId: string | null;
	reusableDraftContext: DraftContext | null;
}>;

export function createPersistentDraftContext(queryClient: QueryClient): DraftContext {
	return {
		authorId: findOrCreateDraftAuthorId({ storage: localStorage, queryClient }),
		draftClient: new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]),
	};
}

export function reportPrewarmPersistenceError(utilityContext: UtilityContext, error: unknown): void {
	void utilityContext.errorReporter.report(
		{ error: new Error('social_starter_image_prewarm_persistence_failed', { cause: error }) },
		{ severity: 'warning' },
	);
}

export function getDraftImage(
	draft: PetitionDraft,
): NonNullable<NonNullable<PetitionDraft['petitionDraft']>['image']> | undefined {
	return draft.petitionDraft?.image ?? draft.recommendedDraft?.image ?? undefined;
}

export async function generateImagePreparation(
	imageDraft: PetitionAngleData,
	utilityContext: UtilityContext,
): Promise<ImagePreparation> {
	const imageJob = await generateAiPetitionImage(
		{ mainPrompt: imageDraft.recommendation, personalStory: imageDraft.why },
		utilityContext,
	);

	return {
		imageGenerationCount: imageJob ? 1 : 0,
		imageJobId: imageJob?.responseId ?? null,
		reusableDraftContext: null,
	};
}
